/* <request-sample>: PDP control that adds qty 1 with `_isSample: true`.
   Visibility tracks the variant's Allows Sample metafield and whether its sample is already in the cart. */
if (!customElements.get('request-sample')) {
  customElements.define(
    'request-sample',
    class RequestSample extends HTMLElement {
      connectedCallback() {
        this.trigger = this.querySelector('[data-request-sample-trigger]');
        this.successMessage = this.querySelector('[data-request-sample-success]');
        this.errorMessage = this.querySelector('[data-request-sample-error]');
        this.form = document.getElementById(this.dataset.formId);
        this.variantAllows = this.parseVariantAllows(
          this.querySelector('[data-request-sample-allows]')?.textContent
        );
        this.sampleInCart = this.dataset.sampleInCart === 'true';
        this.errorFallback = this.dataset.errorMessage || 'Product could not be added to cart';
        this.isSubmitting = false;
        this.latestCart = null;

        this.onTriggerClick = this.onTriggerClick.bind(this);
        this.onVariantChange = this.onVariantChange.bind(this);
        this.onCartUpdate = this.onCartUpdate.bind(this);
        this.onCartRefresh = this.onCartRefresh.bind(this);

        this.trigger?.addEventListener('click', this.onTriggerClick);
        (this.form ?? document).addEventListener('variant:change', this.onVariantChange);
        this.cartUpdateUnsubscriber = theme.pubsub?.subscribe(
          theme.pubsub.PUB_SUB_EVENTS.cartUpdate,
          this.onCartUpdate
        );
        document.addEventListener('cart:refresh', this.onCartRefresh);

        this.syncVisibility();
      }

      disconnectedCallback() {
        this.trigger?.removeEventListener('click', this.onTriggerClick);
        (this.form ?? document).removeEventListener('variant:change', this.onVariantChange);
        this.cartUpdateUnsubscriber?.();
        document.removeEventListener('cart:refresh', this.onCartRefresh);
      }

      get cartDrawer() {
        return document.querySelector('cart-drawer');
      }

      get selectedVariantId() {
        const variantInput = this.form?.querySelector('[name="id"]');
        return String(variantInput?.value || this.dataset.variantId || '');
      }

      parseVariantAllows(raw) {
        try {
          return JSON.parse(raw || '{}');
        } catch (error) {
          console.error('[request-sample] Invalid variant allows map', error);
          return {};
        }
      }

      isTruthySampleProperty(value) {
        if (value === true || value === 1) return true;
        if (typeof value !== 'string') return false;
        const normalized = value.trim().toLowerCase();
        return normalized === 'true' || normalized === '1' || normalized === 'yes';
      }

      variantAllowsSample(variantId) {
        if (!variantId) return false;
        if (Object.prototype.hasOwnProperty.call(this.variantAllows, variantId)) {
          return Boolean(this.variantAllows[variantId]);
        }
        return false;
      }

      cartHasSampleForVariant(cart, variantId) {
        if (!cart?.items?.length || !variantId) return false;

        return cart.items.some((item) => {
          const itemVariantId = String(item.variant_id || item.id || '');
          if (itemVariantId !== String(variantId)) return false;
          return this.isTruthySampleProperty(item.properties?._isSample);
        });
      }

      isSuccessVisible() {
        return Boolean(this.successMessage && !this.successMessage.hasAttribute('hidden'));
      }

      isErrorVisible() {
        return Boolean(this.errorMessage && !this.errorMessage.hasAttribute('hidden'));
      }

      syncVisibility({ showSuccess = false, showError = false } = {}) {
        const variantId = this.selectedVariantId;
        const allowsSample = this.variantAllowsSample(variantId);
        const canShow = allowsSample && !this.sampleInCart;
        // Keep confirmation only while this variant's sample remains in the cart.
        const shouldShowSuccess = Boolean(showSuccess && this.sampleInCart);
        const shouldShowError = Boolean(showError && canShow && !shouldShowSuccess);

        this.toggleAttribute('hidden', !canShow && !shouldShowSuccess);
        this.dataset.variantId = variantId;
        this.dataset.sampleInCart = this.sampleInCart ? 'true' : 'false';

        if (this.trigger) {
          this.trigger.toggleAttribute('hidden', !canShow);
          this.trigger.toggleAttribute('disabled', !canShow || this.isSubmitting);
          this.trigger.setAttribute('aria-disabled', !canShow || this.isSubmitting ? 'true' : 'false');
        }

        if (this.successMessage) {
          this.successMessage.toggleAttribute('hidden', !shouldShowSuccess);
        }

        if (this.errorMessage) {
          this.errorMessage.toggleAttribute('hidden', !shouldShowError);
          if (shouldShowError && !this.errorMessage.textContent.trim()) {
            this.errorMessage.textContent = this.errorFallback;
          }
          if (!shouldShowError) {
            this.errorMessage.textContent = '';
          }
        }
      }

      clearMessages() {
        this.successMessage?.toggleAttribute('hidden', true);
        if (this.errorMessage) {
          this.errorMessage.toggleAttribute('hidden', true);
          this.errorMessage.textContent = '';
        }
      }

      showError() {
        this.successMessage?.toggleAttribute('hidden', true);
        if (!this.errorMessage) return;
        this.errorMessage.textContent = this.errorFallback;
        this.errorMessage.toggleAttribute('hidden', false);
      }

      onVariantChange(event) {
        const variant = event.detail?.variant;
        if (variant?.id) {
          this.dataset.variantId = String(variant.id);
        }

        this.clearMessages();

        if (this.latestCart) {
          this.sampleInCart = this.cartHasSampleForVariant(this.latestCart, this.selectedVariantId);
          this.syncVisibility();
          return;
        }

        this.refreshCartState();
      }

      onCartUpdate(event) {
        if (event?.cart) {
          this.applyCartState(event.cart);
          return;
        }

        this.refreshCartState();
      }

      onCartRefresh() {
        this.refreshCartState();
      }

      applyCartState(cart) {
        const keepSuccess = this.isSuccessVisible();
        const keepError = this.isErrorVisible();
        this.latestCart = cart;
        this.sampleInCart = this.cartHasSampleForVariant(cart, this.selectedVariantId);

        if (!this.sampleInCart) {
          if (!keepError) {
            this.clearMessages();
          }
          this.syncVisibility({ showError: keepError });
          return;
        }

        this.syncVisibility({ showSuccess: keepSuccess });
      }

      async refreshCartState() {
        try {
          const cart = await (
            await fetch(theme.routes.cart_url, {
              ...theme.utils.fetchConfig('json', 'GET'),
            })
          ).json();

          this.applyCartState(cart);
        } catch (error) {
          console.error('[request-sample] Failed to refresh cart state', error);
        }
      }

      async onTriggerClick(event) {
        event.preventDefault();
        if (this.isSubmitting || !this.trigger || this.trigger.hasAttribute('disabled')) return;

        const variantId = this.selectedVariantId;
        if (!variantId || !this.variantAllowsSample(variantId) || this.sampleInCart) {
          this.syncVisibility();
          return;
        }

        this.isSubmitting = true;
        this.clearMessages();
        this.trigger.setAttribute('aria-busy', 'true');
        this.trigger.setAttribute('disabled', '');
        this.trigger.setAttribute('aria-disabled', 'true');

        let sectionsToBundle = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:bundled-sections', {
            bubbles: true,
            detail: { sections: sectionsToBundle },
          })
        );

        const config = theme.utils.fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        config.body = JSON.stringify({
          items: [
            {
              id: Number(variantId),
              quantity: 1,
              properties: {
                _isSample: true,
              },
            },
          ],
          sections: sectionsToBundle,
          sections_url: window.location.pathname,
        });

        try {
          const parsedState = await (await fetch(theme.routes.cart_add_url, config)).json();

          if (parsedState.status) {
            this.showError();
            await this.refreshCartState();
            document.dispatchEvent(
              new CustomEvent('cart:refresh', {
                bubbles: true,
                detail: { open: false },
              })
            );
            return;
          }

          const cartJson = await (
            await fetch(theme.routes.cart_url, {
              ...theme.utils.fetchConfig('json', 'GET'),
            })
          ).json();
          cartJson.sections = parsedState.sections;

          this.latestCart = cartJson;
          this.sampleInCart = this.cartHasSampleForVariant(cartJson, variantId);
          this.syncVisibility({ showSuccess: true });

          theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
            source: 'request-sample',
            productVariantId: variantId,
            cart: cartJson,
          });

          document.dispatchEvent(
            new CustomEvent('ajaxProduct:added', {
              detail: {
                product: parsedState,
              },
            })
          );

          if (document.body.classList.contains('template-cart') || theme.settings.cartType === 'page') {
            window.location.href = theme.routes.cart_url;
            return;
          }

          this.cartDrawer?.show(this.trigger);
        } catch (error) {
          console.error('[request-sample] Failed to add sample', error);
          this.showError();
        } finally {
          this.isSubmitting = false;
          this.trigger?.removeAttribute('aria-busy');
          this.syncVisibility({
            showSuccess: this.isSuccessVisible(),
            showError: this.isErrorVisible(),
          });
        }
      }
    }
  );
}
